import { Signal } from "@suse-o11y/signal";
import React from "react";

import type { TimeRange } from "../../../app/state/view/timeline/TimeRange";
import { ButtonGroup } from "../buttons/Button";
import { useNavigateFrom } from "../link";

import { TimeRangeSelector } from "./timerange/selector/TimeRangeSelector";
import {
	TimeRangeSelectorButtonShiftLeft,
	TimeRangeSelectorButtonShiftRight,
} from "./timerange/selector/TimeRangeSelectorButtonShift";
import { ZoomOut } from "./timerange/ZoomOut";
import { useTimeRangeHistory } from "./time-range-history-utils";

export interface TimelineRangeSelectorExtendedProps extends Partial<ContainerProps> {
	disabled?: boolean;
	contextName: string;
	timeRange: Signal<TimeRange>;
}

export const TimelineRangeSelectorExtended: React.FC<TimelineRangeSelectorExtendedProps> = ({
	layout = "wide",
	disabled = false,
	contextName,
	timeRange: timeRangeFromProps,
	...props
}) => {
	const size = layout === "compact" ? "small" : "middle";
	const navigateFrom = useNavigateFrom();

	const timeRangeHistory = useTimeRangeHistory();
	const timeRange = React.useMemo(() => {
		return Signal(timeRangeFromProps, (next) => {
			/**
			 * Custom time range interval should be pushed to recently used.
			 * The recently used list should be available everywhere, and should be updated by all custom intervals used anywhere.
			 * That is because if user do an investigation, and he use some custom intervals in some popups, drawers, pages, he need these last used intervals available on the timeline as well.
			 */
			if (next.isSpecific()) {
				timeRangeHistory?.write((current) => current.add(next));
			}

			timeRangeFromProps.write(next);
		});
	}, [timeRangeFromProps, timeRangeHistory]);

	const onClear = React.useCallback(() => {
		navigateFrom("*", {
			search: (search) => ({
				...search,
				timeRange: undefined,
				timestamp: undefined,
			}),
		});
	}, [navigateFrom]);

	return (
		<Container data-testid="timerange-selector" layout={layout} {...props}>
			<TimeRangeSelectorButtonShiftLeft
				disabled={disabled}
				size={size}
				timeRange={timeRange}
				tooltip={`Shift the ${contextName} interval to the left`}
			/>

			<TimeRangeSelector
				align={{ offset: layout === "compact" ? [-28, 8] : [-57, 10] }}
				contextName={contextName}
				disabled={disabled}
				onClear={onClear}
				size={size}
				timeRange={timeRange}
			/>

			<TimeRangeSelectorButtonShiftRight
				disabled={disabled}
				size={size}
				timeRange={timeRange}
				tooltip={`Shift the ${contextName} interval to the right`}
			/>

			<ZoomOut
				className={layout === "compact" ? "ml-1" : undefined}
				contextName={contextName}
				disabled={disabled}
				size={size}
				timeRange={timeRange}
			/>
		</Container>
	);
};

interface ContainerProps {
	layout: "compact" | "wide";
}

const Container: React.FC<React.PropsWithChildren<ContainerProps>> = ({ layout, ...props }) => {
	if (layout === "wide") {
		return <ButtonGroup {...props} className="flex" />;
	}

	return <div {...props} className="flex gap-1" />;
};
